import React from "react";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  IconButton,
  Button,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { FlowCondition } from "../../../../../stores/flowsStore";
import { useTriggerConditions } from "./useTriggerConditions";

interface TriggerConditionsProps {
  conditions: FlowCondition[];
  conditionsLogic: string;
  onChange: (conditions: FlowCondition[], logic: string) => void;
}

export const TriggerConditions: React.FC<TriggerConditionsProps> = ({
  conditions,
  conditionsLogic,
  onChange,
}) => {
  const {
    flowFields,
    operators,
    handleAddCondition,
    handleRemoveCondition,
    handleConditionChange,
    handleLogicChange,
    getFieldOptions,
    getFieldType,
  } = useTriggerConditions({ conditions, conditionsLogic, onChange });

  const renderValueInput = (condition: FlowCondition, index: number) => {
    const fieldType = getFieldType(condition.field);
    const options = getFieldOptions(condition.field);

    // Don't show value for is_empty/is_not_empty operators
    if (condition.operator === "is_empty" || condition.operator === "is_not_empty") {
      return null;
    }

    if (fieldType === "select" || options.length > 0) {
      return (
        <TextField
          select
          size="small"
          label="Value"
          value={condition.value}
          onChange={(e) => handleConditionChange(index, "value", e.target.value)}
          sx={{ minWidth: 180 }}
        >
          {options.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    if (fieldType === "number") {
      return (
        <TextField
          type="number"
          size="small"
          label="Value"
          value={condition.value}
          onChange={(e) => handleConditionChange(index, "value", e.target.value)}
          sx={{ minWidth: 120 }}
        />
      );
    }

    return (
      <TextField
        size="small"
        label="Value"
        value={condition.value}
        onChange={(e) => handleConditionChange(index, "value", e.target.value)}
        sx={{ minWidth: 180 }}
      />
    );
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Button startIcon={<AddIcon />} onClick={handleAddCondition} size="small">
          Add Condition
        </Button>
      </Box>

      {conditions.length === 0 && (
        <Typography color="text.secondary" sx={{ textAlign: "center", py: 2 }}>
          No conditions set. Click "Add Condition" to create trigger rules.
        </Typography>
      )}

      {conditions.length > 1 && (
        <Box sx={{ mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Logic</InputLabel>
            <Select
              value={conditionsLogic}
              label="Logic"
              onChange={(e) => handleLogicChange(e.target.value)}
            >
              <MenuItem value="AND">ALL (AND)</MenuItem>
              <MenuItem value="OR">ANY (OR)</MenuItem>
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            {conditionsLogic === "AND"
              ? "All conditions must be met"
              : "At least one condition must be met"}
          </Typography>
        </Box>
      )}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {conditions.map((condition, index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              p: 1.5,
              bgcolor: "background.default",
              borderRadius: 1,
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ minWidth: 30 }}
            >
              {index + 1}.
            </Typography>

            <TextField
              select
              size="small"
              label="Field"
              value={condition.field}
              onChange={(e) => handleConditionChange(index, "field", e.target.value)}
              sx={{ minWidth: 180 }}
            >
              {flowFields.map((field) => (
                <MenuItem key={field.key} value={field.key}>
                  {field.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              size="small"
              label="Operator"
              value={condition.operator}
              onChange={(e) => handleConditionChange(index, "operator", e.target.value)}
              sx={{ minWidth: 150 }}
            >
              {operators.map((op) => (
                <MenuItem key={op.value} value={op.value}>
                  {op.label}
                </MenuItem>
              ))}
            </TextField>

            {renderValueInput(condition, index)}

            <Box sx={{ flex: 1 }} />

            <IconButton
              size="small"
              onClick={() => handleRemoveCondition(index)}
              color="error"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
